import React, { useEffect, useState, useRef } from 'react';
import css from '../../../../../../../common/chartMagneticStyle.less';
import { am4core, am4charts } from "../../../../../../../loaders/amchartsLoader";
import { PropTypes } from 'prop-types';
import { format } from 'date-fns';
import { colors } from "../../../../../../../utils/colors";
import { customizeGrip, enableScrollbarGripKeyboard, formatDateInAMPM, getSpace } from "../../../common/adUtils";
import { marker, charRect, EXPORT_CSV_LABELS } from "../../../../../../../utils/enums";
import { displayPathAnalyticsTime , shortDisplayTime } from '../../../../../../../utils/displayTime';
import { exportCSV } from "../../../../../../../utils/chart";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { reloadQSI } from "../../../../../../../utils/common";
import useI18n from '../../../../../../../i18n/useI18n';
import { initChartThemeSupport } from "../../../../../../../common/Theme";


const [
    HbrIconButton,
    HbrTooltip
] = reactWrapper(["hbr-icon-button", "hbr-tooltip"]);
const BarLineCharts = (props) => {
    const { tViews } = useI18n('views')
    const [preSelected, setPreSelected] = useState(props.selectedBar);
    const currentChartRef = useRef();
    const [timeStampLabel, setTimeStampLabel] = useState();

    useEffect(() => {
        if(preSelected !== props.selectedBar) reloadQSI();
    }, [preSelected]);

    const highlighColumn = (selectedDate, chart) => {
        chart.series.each(function(series) {
        if (series instanceof am4charts.ColumnSeries) {
            series.columns.each(function(column) {
            if(selectedDate) {
                if(displayPathAnalyticsTime(column.dataItem.dateX) !== selectedDate) {
                    column.setState("default");
                    column.isActive = false;
                } else {
                    column.isActive = true;
                    // For Qualtrics - Save this active state in window object
                    window._selected_date_hour = column.dataItem._dataContext.entry_ts;
                    window._ad_usage = column.dataItem._dataContext.display_cellular_usage;
                    window._anomalous_circuits = column.dataItem._dataContext.anomalous_circuits_cnt;
                    window._tab_view = "-"; // initiate tab_view to "-"
                }
            }
            })
        }
        })
    }

    const addTooltip = (series) => {
        series.adapter.add("tooltipText", (text, target) => {
            const dataItem = target.tooltipDataItem;
            if (dataItem && dataItem.dataContext) {
                const value = dataItem.dataContext;
                const tooltipTimestamp = formatDateInAMPM(value.entry_ts);
                let tooltipContent = `${tViews('postSpace')}[bold]${tooltipTimestamp}[/]\n\n`;
                tooltipContent += `[${colors.gray90}]${charRect}[/]${getSpace(4, '')}${tViews('usageAcrossAllCellularCircuits')} ${tViews('preSpace')} ${getSpace(10, '')}${value.display_cellular_usage}\n\n`;
                tooltipContent += `[${colors.rose40}]${marker}[/]${getSpace(2, '')}${tViews('anomalousCircuitswithColon')} ${tViews('preSpace')} ${value["Anomalous Circuits"]}% ${tViews('postSpace')} (${value["anomalous_circuits_cnt"]} / ${value["anomalous_circuits_total_cnt"]})\n\n`;
                return tooltipContent.trim();
            }
            return text;
        });
    }

    const addScrollbar = (chart, circuitSeries, usageSeries) => {
        chart.scrollbarX = new am4charts.XYChartScrollbar();
        chart.zoomOutButton.disabled = true;
        chart.scrollbarX.background.fill = am4core.color(colors.blue70);
        chart.scrollbarX.startGrip.visible = true;
        chart.scrollbarX.endGrip.visible = true;
        chart.scrollbarX.disabled = false;
        chart.scrollbarX.thumb.minWidth = 10;
        const startLine = chart.scrollbarX.startGrip.createChild(am4core.Rectangle);
        startLine.height = 30;
        startLine.width = 5;
        startLine.fillOpacity = 0.4;
        startLine.align = "center";
        startLine.valign = "middle";
        const endLine = chart.scrollbarX.endGrip.createChild(am4core.Rectangle);
        endLine.height = 30;
        endLine.width = 5;
        endLine.fillOpacity = 0.4;
        endLine.align = "center";
        endLine.valign = "middle";
        chart.scrollbarX.background.fillOpacity = 0.5;
        if(usageSeries && circuitSeries) {
            chart.scrollbarX.series.push(usageSeries);
            chart.scrollbarX.series.push(circuitSeries);
            var scrollSeries = chart.scrollbarX.scrollbarChart.series.getIndex(0);
            scrollSeries.strokeDasharray = "2,4";
        }
        chart.scrollbarX.parent = chart.bottomAxesContainer;
        chart.scrollbarX.minHeight = 30;
        chart.scrollbarX.thumb.minWidth = 50;
        enableScrollbarGripKeyboard(chart.scrollbarX, {
            insertBeforeChart: true,
            labelPrefix: circuitSeries?.yAxis?.title?.text
        });

        customizeGrip(chart.scrollbarX.startGrip, am4core, colors);
        customizeGrip(chart.scrollbarX.endGrip, am4core, colors);

        // Customize scrollbar thumb
        chart.scrollbarX.background.fill = am4core.color(colors.blue45);
        chart.scrollbarX.background.fillOpacity = 0.5;
    }

    const addCursor = (chart, dateAxis) => {
        chart.cursor = new am4charts.XYCursor();
        chart.cursor.behavior = "zoomX"; // or "panX" or default
        chart.cursor.xAxis = dateAxis;
        chart.cursor.lineX.fill = am4core.color(colors.gray0);
        chart.cursor.lineX.fillOpacity = 0.1;
    }

    const addLegend = (chart) => {
        chart.legend = new am4charts.Legend();
        chart.legend.position = "bottom";
        chart.legend.contentAlign = "right";
        chart.legend.paddingBottom = 8;

        // Add tooltip for legend items
        chart.legend.itemContainers.template.tooltipText = tViews('clickToToggle');
        chart.legend.itemContainers.template.tooltipPosition = "pointer";

        // Make legend items focusable
        chart.legend.itemContainers.template.focusable = true;
    }

    const createCircuitBullets = (circuitSeries) => {
        var circuitBullet = circuitSeries.bullets.push(new am4charts.Bullet());
        var circuitCircle = circuitBullet.createChild(am4core.Circle);
        circuitBullet.horizontalCenter = "middle";
        circuitBullet.verticalCenter = "middle";
        circuitCircle.radius = 2.5;
        circuitCircle.fill = colors.rose40;
        circuitCircle.stroke = colors.gray100;
        circuitCircle.strokeWidth = 0.5;

        var circuitState = circuitBullet.states.create("hover");
        circuitState.properties.scale = 1.2;

        return circuitBullet;
    }

    const createCircuitSeries = (chart, circuitAxis) => {
        let circuitSeries = chart.series.push(new am4charts.LineSeries());
        circuitSeries.dataFields.valueY = "Anomalous Circuits";
        circuitSeries.dataFields.dateX = "entry_ts";
        circuitSeries.yAxis = circuitAxis;
        circuitSeries.name = tViews('anomalousCircuits');
        circuitSeries.strokeWidth = 2;
        circuitSeries.showOnInit = false;
        circuitSeries.propertyFields.strokeDasharray = "dashLength";
        circuitSeries.connect = false;
        circuitSeries.fill = colors.rose40;
        circuitSeries.stroke = colors.rose40;
        circuitSeries.defaultState.transitionDuration = 0;
        circuitSeries.hiddenState.transitionDuration = 0;

        return circuitSeries;
    }

    const createUsageSeries = (chart, usageAxis) => {
        let usageAxisSeries = chart.series.push(new am4charts.ColumnSeries());
        usageAxisSeries.dataFields.valueY = "Usage";
        usageAxisSeries.dataFields.dateX = "entry_ts";
        usageAxisSeries.yAxis = usageAxis;
        usageAxisSeries.name = tViews('applicationTableViewUsage');
        usageAxisSeries.showOnInit = false;
        usageAxisSeries.columns.template.propertyFields.strokeDasharray = "dashLength";
        usageAxisSeries.columns.template.propertyFields.fillOpacity = "alpha";
        usageAxisSeries.showOnInit = true;
        usageAxisSeries.fill = colors.gray90;
        usageAxisSeries.stroke = colors.gray90;

        var columnTemplate = usageAxisSeries.columns.template;
        columnTemplate.width = am4core.percent(50);
        columnTemplate.tooltipText = "{name}: [bold]{valueY}[/]";

        var activeState = columnTemplate.states.create("active");
        activeState.properties.stroke = am4core.color(colors.gray0); // white border
        activeState.properties.strokeWidth = 2;

        columnTemplate.events.on("hit", function hit(ev) {
            let selectedDate = displayPathAnalyticsTime(ev.target.column._dataItem.dates.dateX);
            props.saveData(selectedDate);
            setPreSelected(selectedDate);
            highlighColumn(selectedDate, chart);
        });

        usageAxisSeries.tooltip.getFillFromObject = false;
        usageAxisSeries.tooltip.background.fill = colors.gray100;
        usageAxisSeries.tooltip.background.stroke = am4core.color(colors.gray15);
        usageAxisSeries.tooltip.autoTextColor = false;
        usageAxisSeries.tooltip.label.fill = colors.gray15;

        usageAxisSeries.events.on("ready", () => {
            highlighColumn(preSelected, chart);
        })

        // Use adapter to format tooltip text
        usageAxisSeries.columns.template.adapter.add("tooltipText", function (text, target) {
            let dataItem = target.dataItem;
            if(dataItem?.dataContext) {
                let date = format(dataItem.dateX, "yyyy-MM-dd, h:mm a");
                let tooltipContent = `${tViews('postSpace')}[bold]${date}[/]\n\n`;
                tooltipContent += `[${colors.gray90}]${charRect}[/]${getSpace(4, '')}${tViews('usageAcrossAllCellularCircuits')} ${tViews('preSpace')} ${getSpace(10, '')}${dataItem.dataContext.display_cellular_usage}\n\n`;
                tooltipContent += `[${colors.rose40}]${marker}[/]${getSpace(2, '')}${tViews('anomalousCircuitswithColon')} ${tViews('preSpace')} ${dataItem.dataContext["Anomalous Circuits"]}% ${tViews('postSpace')} (${dataItem.dataContext["anomalous_circuits_cnt"]} / ${dataItem.dataContext["anomalous_circuits_total_cnt"]})\n\n`;
                return tooltipContent.trim();
            }
        });

        return usageAxisSeries;
    }

    const createCircuitAxis = (chart, usageAxis) => {
        let circuitAxis = chart.yAxes.push(new am4charts.ValueAxis());
        circuitAxis.title.text = tViews('usageBasedAD.anomalousCircuits');
        circuitAxis.syncWithAxis = usageAxis;
        circuitAxis.min = 0;
        circuitAxis.cursorTooltipEnabled = false;
        // Add this line to show plain numbers
        circuitAxis.numberFormatter = new am4core.NumberFormatter();
        circuitAxis.numberFormatter.numberFormat = "#";

        return circuitAxis
    }

    const createUsageAxis = (chart) => {
        let usageAxis = chart.yAxes.push(new am4charts.ValueAxis());
        usageAxis.title.text = tViews('usageBasedAD.usageGb');
        usageAxis.min = 0;
        usageAxis.renderer.opposite = true
        usageAxis.numberFormatter.numberFormat = "#.0b";
        usageAxis.renderer.grid.template.disabled = false;
        usageAxis.cursorTooltipEnabled = false;
        return usageAxis;
    }

    const createDateAxis = (chart) => {
        // Create axes
        let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.minGridDistance = 50;
        dateAxis.renderer.grid.template.disabled = false;
        dateAxis.tooltip.disabled = true;

        return dateAxis
    }

    const createChart = (data) => {
        // Create chart instance
        var chart = am4core.create(props.id, am4charts.XYChart);

        chart.colors.step = 2;
        chart.maskBullets = false;

        // Add data
        chart.data = data

        // create X-axis
        let dateAxis = createDateAxis(chart);

        // create Y-axis
        let usageAxis = createUsageAxis(chart);
        let circuitAxis = createCircuitAxis(chart, usageAxis);

        // Create series
        let usageSeries = createUsageSeries(chart, usageAxis);
        let circuitSeries = createCircuitSeries(chart, circuitAxis);

        //Create Bullets
        let circuitBullet = createCircuitBullets(circuitSeries);
        // Add click event
        circuitBullet.events.on("hit", function(ev) {
            const selectedDate = displayPathAnalyticsTime(ev.target.dataItem.dataContext.entry_ts);
            props.saveData(selectedDate);
            setPreSelected(selectedDate);
            highlighColumn(selectedDate, chart);
        });

        // Add legend
        addLegend(chart);

        // Add cursor
        addCursor(chart, dateAxis);

        // Add scrollbar
        addScrollbar(chart, circuitSeries, usageSeries);

        // Add tooltip
        addTooltip(usageSeries);
             // Initialize chart theme support
     initChartThemeSupport(chart, am4core, props);
        return chart;
    }
 
    useEffect(() => {
        if (!props.data || !props.data.length) return;
        let chart = createChart(props.data);
        currentChartRef.current = chart;
        setTimeStampLabel(shortDisplayTime(props.timePeriod?.[0]) + " - " + shortDisplayTime(props.timePeriod?.[1]));
        return () => {
            chart?.dispose();
        }
    }, [props.data, props.id]);

      const onDownloadCsvClick = () => {
        if (currentChartRef.current && props.id) {
           exportCSV(currentChartRef.current, EXPORT_CSV_LABELS.AD_CELLULAR_USAGE);
        }
      };

    return (
        <>
             <div id="usage-based-ad-download-csv">
                <HbrTooltip content={tViews('downloadCSV')}>
                    <span>
                        <HbrIconButton
                            size="20px"
                            name="file-csv"
                            data-testid="DownloadCsvBtn"
                            onClick={() => onDownloadCsvClick()}
                            arialabel={ tViews('downloadChartDisplaying') + props.id.toLowerCase() + tViews('downloadCSVInfo') + timeStampLabel}
                        />
                    </span>
                </HbrTooltip>
            </div>  
            <div
                id={props.id}
                className={`${css.chart} hbr-type-body4`}
                role="region"
                aria-label={props.id}
                tabIndex={0}
            />
        </>
    );
}

BarLineCharts.propTypes = {
    id: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    selectedBar: PropTypes.string,
    saveData: PropTypes.func,
    timePeriod: PropTypes.array
}

export default BarLineCharts;
